/**
 * Notification Preferences Component
 *
 * Settings panel for managing notification preferences
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useNotificationPreferences,
  useUpdateNotificationPreferences,
} from '@/hooks/useNotifications';
import {
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
  isPushSupported,
  getNotificationPermission,
  isSubscribedToPush,
} from '@/lib/pushNotifications';
import { ReleaseDigestMode, UpdateNotificationPreferencesRequest } from '@/types/notification';
import { Select } from '@/components/ui/Select';
import { useAuth } from '@/hooks/useAuth';
import { useTimezone } from '@/contexts/TimezoneContext';

interface ToggleSwitchProps {
  enabled: boolean;
  onChange: (enabled: boolean) => void;
  disabled?: boolean;
  label: string;
  description?: string;
}

const ToggleSwitch = ({ enabled, onChange, disabled, label, description }: ToggleSwitchProps) => (
  <div className="flex items-start justify-between py-4 border-b border-gray-100 last:border-b-0">
    <div className="flex-1 pr-4">
      <label className="text-sm font-medium text-gray-900">{label}</label>
      {description && <p className="text-sm text-gray-500 mt-1">{description}</p>}
    </div>
    <button
      type="button"
      onClick={() => !disabled && onChange(!enabled)}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus-visible:border-primary-700 ${
        enabled ? 'bg-primary-500' : 'bg-gray-200'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      role="switch"
      aria-label={label}
      aria-checked={enabled}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
          enabled ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  </div>
);

export const NotificationPreferences = () => {
  const { t } = useTranslation('common');
  const { data: preferences, isLoading } = useNotificationPreferences();
  const updatePreferences = useUpdateNotificationPreferences();
  const { user } = useAuth();

  // Determine if agency-only features should be shown
  // Show if user is agency staff OR if their team is a client of an agency
  const showAgencyFeatures = user?.isAgencyStaff || !!user?.team?.agencyId;

  const [pushSupported] = useState(isPushSupported());
  const [pushPermission, setPushPermission] = useState<NotificationPermission>(
    getNotificationPermission()
  );
  const [isPushSubscribed, setIsPushSubscribed] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushError, setPushError] = useState<string | null>(null);

  const { timezone } = useTimezone();

  const hourOptions = Array.from({ length: 24 }, (_, h) => {
    const label = `${String(h).padStart(2, '0')}:00`;
    return { value: String(h), label };
  });

  // Check push subscription status on mount
  useEffect(() => {
    const checkPushStatus = async () => {
      const subscribed = await isSubscribedToPush();
      setIsPushSubscribed(subscribed);
    };
    checkPushStatus();
  }, []);

  const handleToggle = (
    key: keyof UpdateNotificationPreferencesRequest,
    value: boolean
  ) => {
    updatePreferences.mutate({ [key]: value });
  };

  const handleDigestModeChange = (mode: ReleaseDigestMode) => {
    if (mode === 'DAILY') {
      updatePreferences.mutate({
        releaseDigestMode: mode,
        releaseDigestHour: preferences?.releaseDigestHour ?? 8,
      });
    } else {
      updatePreferences.mutate({ releaseDigestMode: mode });
    }
  };

  const handleDigestHourChange = (hour: string) => {
    updatePreferences.mutate({ releaseDigestMode: 'DAILY', releaseDigestHour: Number(hour) });
  };

  const handleEnablePush = async () => {
    setPushLoading(true);
    setPushError(null);

    const result = await subscribeToPushNotifications();

    if (result.success) {
      setIsPushSubscribed(true);
      setPushPermission('granted');
      // Also update backend preference
      updatePreferences.mutate({ pushEnabled: true });
    } else {
      setPushError(result.error || t('notif_pref_push_enable_failed'));
    }

    setPushLoading(false);
  };

  const handleDisablePush = async () => {
    setPushLoading(true);
    setPushError(null);

    const result = await unsubscribeFromPushNotifications();

    if (result.success) {
      setIsPushSubscribed(false);
      // Also update backend preference
      updatePreferences.mutate({ pushEnabled: false });
    } else {
      setPushError(result.error || t('notif_pref_push_disable_failed'));
    }

    setPushLoading(false);
  };

  if (isLoading) {
    return (
      <div className="p-6 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-700 mx-auto"></div>
        <p className="mt-2 text-gray-500">{t('notif_pref_loading_text')}</p>
      </div>
    );
  }

  if (!preferences) {
    return (
      <div className="p-6 text-center text-gray-500">
        {t('notification_prefs_loading')}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="px-5 sm:px-6 py-4 border-b border-gray-200">
        <h2 className="text-base sm:text-lg font-semibold text-gray-900">{t('notification_prefs_title')}</h2>
        <p className="text-sm text-gray-500 mt-1">
          {t('notification_prefs_subtitle')}
        </p>
      </div>

      <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
        <ToggleSwitch
          label={t('release_celebrations_setting')}
          description={t('release_celebrations_setting_description')}
          enabled={preferences.celebrationsEnabled ?? true}
          onChange={(enabled) => handleToggle('celebrationsEnabled', enabled)}
        />
      </div>

      {/* Push Notifications Section */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
        <h3 className="text-sm sm:text-base font-medium text-gray-900 mb-3 sm:mb-4">{t('notification_prefs_push_title')}</h3>

        {!pushSupported ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              {t('notification_prefs_push_not_supported')}
            </p>
          </div>
        ) : pushPermission === 'denied' ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-800">
              {t('notification_prefs_push_blocked')}
            </p>
          </div>
        ) : (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <div>
                <p className="text-sm font-medium text-gray-900">{t('notification_prefs_push_enable')}</p>
                <p className="text-sm text-gray-500">
                  {t('notification_prefs_push_description')}
                </p>
              </div>
              <button
                onClick={isPushSubscribed ? handleDisablePush : handleEnablePush}
                disabled={pushLoading}
                className={`w-full sm:w-auto px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  isPushSubscribed
                    ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    : 'bg-primary-500 text-primary-contrast hover:bg-primary-600'
                } disabled:opacity-50`}
              >
                {pushLoading ? t('notif_pref_btn_loading') : isPushSubscribed ? t('notif_pref_push_btn_disable') : t('notif_pref_push_btn_enable')}
              </button>
            </div>

            {pushError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-red-800">{pushError}</p>
              </div>
            )}

            {isPushSubscribed && (
              <div className="space-y-1 pl-4 border-l-2 border-gray-200">
                <ToggleSwitch
                  label={t('notif_pref_post_published_label')}
                  description={t('notif_pref_post_published_push_desc')}
                  enabled={preferences.pushPostPublished}
                  onChange={(v) => handleToggle('pushPostPublished', v)}
                />
                <ToggleSwitch
                  label={t('notif_pref_post_failed_label')}
                  description={t('notif_pref_post_failed_push_desc')}
                  enabled={preferences.pushPostFailed}
                  onChange={(v) => handleToggle('pushPostFailed', v)}
                />
                {showAgencyFeatures && (
                  <>
                    <ToggleSwitch
                      label={t('notif_pref_release_label')}
                      description={t('notif_pref_release_push_desc')}
                      enabled={preferences.pushApprovalRequests}
                      onChange={(v) => handleToggle('pushApprovalRequests', v)}
                    />
                    <ToggleSwitch
                      label={t('notif_pref_resubmitted_label')}
                      description={t('notif_pref_resubmitted_push_desc')}
                      enabled={preferences.pushApprovalResubmitted ?? true}
                      onChange={(v) => handleToggle('pushApprovalResubmitted', v)}
                    />
                    <ToggleSwitch
                      label={t('notif_pref_comments_label')}
                      description={t('notif_pref_comments_push_desc')}
                      enabled={preferences.pushComments}
                      onChange={(v) => handleToggle('pushComments', v)}
                    />
                    <ToggleSwitch
                      label={t('notif_pref_mentions_label')}
                      description={t('notif_pref_mentions_push_desc')}
                      enabled={preferences.pushMentions ?? true}
                      onChange={(v) => handleToggle('pushMentions', v)}
                    />
                  </>
                )}
                <ToggleSwitch
                  label={t('notif_pref_channel_alerts_label')}
                  description={t('notif_pref_channel_alerts_push_desc')}
                  enabled={preferences.pushChannelAlerts}
                  onChange={(v) => handleToggle('pushChannelAlerts', v)}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Email Notifications Section */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
        <h3 className="text-sm sm:text-base font-medium text-gray-900 mb-3 sm:mb-4">{t('notification_prefs_email_title')}</h3>

        <ToggleSwitch
          label={t('notif_pref_digest_alert_email_label')}
          description={t('notif_pref_digest_alert_email_desc')}
          enabled={preferences.digestEmailEnabled}
          onChange={(v) => handleToggle('digestEmailEnabled', v)}
        />

        <ToggleSwitch
          label={t('notif_pref_post_published_label')}
          description={t('notif_pref_post_published_email_desc')}
          enabled={preferences.emailPostPublished}
          onChange={(v) => handleToggle('emailPostPublished', v)}
        />
        <ToggleSwitch
          label={t('notif_pref_post_failed_label')}
          description={t('notif_pref_post_failed_email_desc')}
          enabled={preferences.emailPostFailed}
          onChange={(v) => handleToggle('emailPostFailed', v)}
        />
        {showAgencyFeatures && (
          <>
            <ToggleSwitch
              label={t('notif_pref_comments_label')}
              description={t('notif_pref_comments_email_desc')}
              enabled={preferences.emailComments}
              onChange={(v) => handleToggle('emailComments', v)}
            />
          </>
        )}
        <ToggleSwitch
          label={t('notif_pref_channel_alerts_label')}
          description={t('notif_pref_channel_alerts_email_desc')}
          enabled={preferences.emailChannelAlerts}
          onChange={(v) => handleToggle('emailChannelAlerts', v)}
        />

        {/* Release email digest */}
        <div className="py-4 border-t border-gray-100 mt-4" role="radiogroup" aria-labelledby="release-digest-title">
            <p id="release-digest-title" className="text-sm font-medium text-gray-900">
              {t('notif_pref_digest_title', { defaultValue: 'Release email digest' })}
            </p>
            <p className="text-sm text-gray-500 mt-1 mb-3">
              {t('notif_pref_digest_desc', {
                defaultValue:
                  'One email with everything waiting for your release, grouped by brand. Only sent when something is new.',
              })}
            </p>
            <div className="space-y-2">
              {(
                [
                  {
                    mode: 'HOURLY' as const,
                    label: t('notif_pref_digest_hourly', { defaultValue: 'Hourly' }),
                    hint: t('notif_pref_digest_hourly_hint', {
                      defaultValue: 'Only when something is new, quiet at night 22:00–07:00',
                    }),
                  },
                  {
                    mode: 'DAILY' as const,
                    label: t('notif_pref_digest_daily', { defaultValue: 'Daily' }),
                    hint: t('notif_pref_digest_daily_hint', {
                      defaultValue: 'One email a day at the hour you choose',
                    }),
                  },
                  {
                    mode: 'OFF' as const,
                    label: t('notif_pref_digest_off', { defaultValue: 'Off' }),
                    hint: t('notif_pref_digest_off_hint', {
                      defaultValue: 'No release emails. Push and in-app still work.',
                    }),
                  },
                ]
              ).map((option) => {
                const checked = (preferences.releaseDigestMode ?? 'HOURLY') === option.mode;
                return (
                  <label
                    key={option.mode}
                    className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                      checked ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="releaseDigestMode"
                      value={option.mode}
                      checked={checked}
                      onChange={() => handleDigestModeChange(option.mode)}
                      className="mt-1 h-4 w-4 accent-primary-600 focus:outline-none"
                    />
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-gray-900">{option.label}</span>
                      <span className="block text-sm text-gray-500">{option.hint}</span>
                      {option.mode === 'DAILY' && checked && (
                        <span className="mt-2 flex flex-wrap items-center gap-2">
                          <span className="text-sm text-gray-700">
                            {t('notif_pref_digest_at', { defaultValue: 'At' })}
                          </span>
                          <span className="w-28">
                            <Select
                              value={String(preferences.releaseDigestHour ?? 8)}
                              onValueChange={handleDigestHourChange}
                              options={hourOptions}
                              size="sm"
                            />
                          </span>
                          <span className="text-xs text-gray-500">
                            {t('notif_pref_digest_timezone', {
                              defaultValue: 'your time ({{timezone}})',
                              timezone,
                            })}
                          </span>
                        </span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
        </div>
      </div>

      {/* In-App Notifications Section */}
      <div className="px-4 sm:px-6 py-4">
        <h3 className="text-sm sm:text-base font-medium text-gray-900 mb-3 sm:mb-4">{t('notification_prefs_inapp_title')}</h3>

        <ToggleSwitch
          label={t('notif_pref_inapp_enable_label')}
          description={t('notif_pref_inapp_enable_desc')}
          enabled={preferences.inAppEnabled}
          onChange={(v) => handleToggle('inAppEnabled', v)}
        />
      </div>

      {/* Save Status */}
      {updatePreferences.isPending && (
        <div className="px-4 sm:px-6 py-3 bg-soft-mint border-t border-light-teal-gray">
          <p className="text-sm text-primary-800">{t('notif_pref_saving')}</p>
        </div>
      )}

      {updatePreferences.isError && (
        <div className="px-4 sm:px-6 py-3 bg-red-50 border-t border-red-100">
          <p className="text-sm text-red-700">{t('notification_prefs_save_error')}</p>
        </div>
      )}
    </div>
  );
};

export default NotificationPreferences;
